import React from 'react'

import { Times } from 'components/attacks/create/styles'
import CardTooltip from 'components/common/CardTooltip'
import Popover from 'components/common/Popover'
import Text from 'components/common/Text'
import theme from 'theme'

export const styleVariables = {
  height: theme.space[4]
}

const types = {
  blue: {
    color: 'white',
    backgroundColor: theme.colors.blue[0],
    borderColor: 'transparent'
  },

  orange: {
    backgroundColor: theme.colors.yellow[0],
    color: theme.colors.steel[2],
    borderColor: 'transparent'
  },

  red: {
    color: theme.colors.red[0],
    backgroundColor: theme.colors.red[2],
    borderColor: 'transparent'
  },

  gray: {
    color: 'white',
    backgroundColor: theme.colors.steel[1],
    borderColor: 'transparent'
  },

  neutral: {
    color: 'white',
    backgroundColor: theme.colors.steel[0],
    borderColor: 'transparent'
  },

  outline: {
    color: theme.colors.blue[0],
    borderColor: theme.colors.blue[0]
  },

  dark: {
    color: theme.colors.steel[2],
    borderColor: theme.colors.steel[2]
  }
}

const PillRender = React.forwardRef(
  (
    {
      canDestroy,
      children,
      disabled,
      handleClose,
      overflowEllipsis = true,
      showPointer,
      type,
      ...rest
    },
    ref
  ) => (
    <div
      ref={ref}
      css={[
        {
          // 1. This is a one-off from our theme file
          display: 'inline-flex',
          alignItems: 'center',
          position: 'relative',
          paddingLeft: theme.space[2],
          paddingRight: canDestroy ? theme.space[1] : theme.space[2],
          whiteSpace: 'nowrap',
          margin: theme.space[1],
          overflow: 'hidden',
          border: theme.border.base,
          borderRadius: styleVariables.height / 2,
          fontSize: 10, // 1
          fontWeight: theme.fontWeights[1],
          fontFamily: theme.fonts.mono,
          color: theme.colors.steel[1],
          backgroundColor: 'transparent',
          height: styleVariables.height,
          maxWidth: overflowEllipsis && theme.space[8]
        },

        showPointer && !canDestroy && { cursor: 'pointer' },

        type && types[type],

        disabled && {
          opacity: theme.opacity[0],
          cursor: 'not-allowed'
        }
      ]}
      {...rest}
    >
      <Text as="span" overflowEllipsis={overflowEllipsis}>
        {children}
      </Text>
      {canDestroy && (
        <Times
          data-test-pill-close
          inclusive
          css={[
            type === 'orange' && {
              backgroundColor: theme.colors.steel[2],
              color: theme.colors.yellow[0]
            },
            showPointer && { cursor: 'pointer' }
          ]}
          onClick={handleClose}
        />
      )}
    </div>
  )
)

PillRender.displayName = 'PillRender'

type PillProps = {
  canDestroy?: boolean
  children: React.ReactNode
  disabled?: boolean
  handleClose?: () => void
  showPointer?: boolean
  overflowEllipsis?: boolean
  tooltip?: string
  tooltipContents?: () => void | React.ReactNode
  tooltipCss?: Record<string, string>
  popoverOverride?: number
  type?: 'blue' | 'dark' | 'gray' | 'red' | 'outline' | 'neutral' | 'orange'
}
const Pill: React.FC<PillProps & React.HTMLProps<HTMLDivElement>> = ({
  tooltip = '',
  tooltipContents = undefined,
  tooltipCss = null,
  popoverOverride = 0,
  ...rest
}) => {
  if (tooltip || tooltipContents) {
    return (
      <Popover override={popoverOverride}>
        {({ getTargetProps, getPopoverProps }) => (
          <>
            <PillRender
              {...getTargetProps({
                ...rest
              })}
            />
            <CardTooltip
              displayArrow
              size="small"
              variation="dark"
              {...getPopoverProps({
                css: [
                  {
                    maxWidth: theme.space[9]
                  },
                  tooltipCss
                ]
              })}
            >
              {tooltip ? (
                <Text
                  as="small"
                  css={{
                    color: 'white',
                    wordBreak: 'break-all'
                  }}
                  title={tooltip}
                >
                  {tooltip}
                </Text>
              ) : (
                tooltipContents()
              )}
            </CardTooltip>
          </>
        )}
      </Popover>
    )
  } else {
    return <PillRender {...rest} />
  }
}

export default Pill
